import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Shield, Flame, HeartPulse, GraduationCap, Users, Star,
  Phone, ArrowRight, CheckCircle, BadgePercent, Heart
} from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfigContext";
import SEOHead from "@/components/SEOHead";
import { faqSchema } from "@/utils/schemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icons mapped by group index; extra groups fall back to Star
const GROUP_ICONS = [Shield, Star, Flame, HeartPulse, GraduationCap, Users];

export const defaultDiscountContent = {
  discount_label: "10%",
  hero: {
    eyebrow: "Community Appreciation",
    headline_highlight: "— Always.",
    intro: "As a firefighter-owned business, Overhaul Ops is proud to give back to the people who serve and support our communities. That's why we offer a standing discount to community heroes and seniors across the DFW metroplex.",
  },
  groups: [
    { title: "Active-Duty Military & Veterans", desc: "Thank you for your service to our country." },
    { title: "Law Enforcement", desc: "For those who keep our communities safe." },
    { title: "Firefighters & EMS", desc: "From one first-responder family to another." },
    { title: "Nurses", desc: "For the caregivers who look after us all." },
    { title: "Teachers", desc: "For those shaping the next generation." },
    { title: "Seniors (65+)", desc: "A little extra thanks for our community elders." },
  ],
  claim_note: "This is our standing thank-you discount, applied to your final quoted price.",
  how_to_claim: [
    { title: "Request your free quote", desc: "Call us or fill out our contact form for a free, no-obligation quote." },
    { title: "Let us know you qualify", desc: "Mention your eligibility when you reach out. Valid ID or proof of eligibility may be requested." },
    { title: "We apply your discount", desc: "Your discount is applied to your final quoted price — simple and transparent." },
  ],
  faqs: [
    { question: "Who qualifies for the Overhaul Ops community appreciation discount?", answer: "Our discount is available to active-duty military and veterans, law enforcement, firefighters and EMS, nurses, teachers, and seniors 65 and older, on junk removal, hauling, cleanouts, and light demolition throughout the DFW metroplex." },
    { question: "How much is the discount?", answer: "Eligible community heroes and seniors receive 10% off, applied to their final quoted price." },
    { question: "How do I claim the junk removal discount?", answer: "Simply request your free, no-obligation quote and let us know you qualify. Valid ID or proof of eligibility may be requested, and we'll apply the discount to your final quoted price." },
    { question: "Do you offer a military or veteran discount for junk removal in Dallas-Fort Worth?", answer: "Yes. As a firefighter-owned business, Overhaul Ops proudly offers a discount to active-duty military and veterans across the DFW metroplex, including Fort Worth, Denton, Tarrant, Parker, and Wise counties." },
    { question: "Can seniors get a discount on junk hauling?", answer: "Absolutely. Seniors aged 65 and older receive a discount on any junk removal, hauling, or cleanout service as part of our community appreciation program." },
  ],
  cta: {
    heading: "Thank You For What You Do",
    text: "Ready to reclaim your space? Get your free, no-obligation quote today and let us know you qualify for your community appreciation discount.",
  },
};

export default function CommunityDiscount() {
  const { formatted_phone, phone_link } = useSiteConfig();
  const [c, setC] = useState(defaultDiscountContent);

  useEffect(() => {
    axios.get(`${API}/cms/page/community-discount`)
      .then((res) => {
        const doc = res.data?.find?.((s) => s.section === "content");
        if (doc?.content) {
          setC({ ...defaultDiscountContent, ...doc.content });
        }
      })
      .catch(() => { /* use defaults */ });
  }, []);

  const label = c.discount_label || "10%";
  const groups = c.groups?.length ? c.groups : defaultDiscountContent.groups;
  const steps = c.how_to_claim?.length ? c.how_to_claim : defaultDiscountContent.how_to_claim;
  const faqs = c.faqs?.length ? c.faqs : defaultDiscountContent.faqs;

  const discountOffer = {
    "@type": "Offer",
    name: "Community Appreciation Discount",
    description: `${label} off junk removal, hauling, cleanouts, and light demolition for active-duty military, veterans, law enforcement, firefighters, EMS, nurses, teachers, and seniors 65+ in the DFW metroplex.`,
    category: "Discount",
    priceSpecification: { "@type": "PriceSpecification", description: `${label} off the final quoted price` },
    eligibleCustomerType: "Military, Veterans, Law Enforcement, Firefighters, EMS, Nurses, Teachers, Seniors 65+",
    seller: { "@id": "https://overhaulops.com/#localbusiness" },
    areaServed: "Dallas-Fort Worth Metroplex",
  };

  return (
    <div data-testid="community-discount-page" className="pt-20">
      <SEOHead
        title={`Community Appreciation Discount – ${label} Off for Military, First Responders & Seniors`}
        description={`Firefighter-owned Overhaul Ops proudly offers ${label} off junk removal in DFW for active-duty military, veterans, law enforcement, firefighters, EMS, nurses, teachers, and seniors 65+. Applied to your final quote.`}
        keywords="junk removal military discount DFW, first responder discount junk removal Dallas Fort Worth, firefighter owned junk removal discount, veteran junk removal discount Texas, senior discount junk hauling DFW, teacher nurse discount junk removal"
        schemas={[discountOffer, faqSchema(faqs)]}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Community Appreciation Discount", url: "/community-appreciation-discount" }]}
      />

      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#1a1a1a] noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              <Heart className="w-4 h-4" /> {c.hero?.eyebrow || defaultDiscountContent.hero.eyebrow}
            </span>
            <h1
              className="text-5xl md:text-7xl font-black text-white uppercase tracking-wide leading-none mt-3 mb-6"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {label} Off <span className="text-[#b51a00]">{c.hero?.headline_highlight || defaultDiscountContent.hero.headline_highlight}</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {c.hero?.intro || defaultDiscountContent.hero.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact">
                <Button
                  data-testid="discount-hero-quote-btn"
                  className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
                >
                  Get Your Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={phone_link}>
                <Button
                  variant="outline"
                  className="border-2 border-white/40 bg-transparent text-white hover:bg-white hover:text-[#1a1a1a] font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  {formatted_phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Eligible groups */}
      <section data-testid="discount-groups" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">Who Qualifies</span>
            <h2
              className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 text-[#0f0f0f]"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              We Proudly Offer {label} Off For
            </h2>
            <div className="section-divider mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {groups.map((g, i) => {
              const Icon = GROUP_ICONS[i] || Star;
              return (
                <div
                  key={i}
                  data-testid={`discount-group-${i}`}
                  className="bg-[#f4f4f5] p-8 rounded-sm border-l-4 border-[#b51a00] hover:shadow-md transition-shadow"
                >
                  <div className="icon-box rounded-sm mb-4 w-14 h-14">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3
                    className="text-xl font-bold uppercase tracking-tight mb-2 text-[#0f0f0f]"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                  >
                    {g.title}
                  </h3>
                  <p className="text-[#4a4a4a]">{g.desc}</p>
                </div>
              );
            })}
          </div>

          <p className="text-center text-[#4a4a4a] mt-12 max-w-2xl mx-auto italic">
            {c.claim_note || defaultDiscountContent.claim_note}
          </p>
        </div>
      </section>

      {/* How to claim */}
      <section data-testid="discount-how-to-claim" className="py-20 md:py-28 bg-[#f4f4f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">Easy & Transparent</span>
            <h2
              className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 text-[#0f0f0f]"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              How To Claim Your Discount
            </h2>
            <div className="section-divider mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={i} data-testid={`discount-step-${i}`} className="bg-white p-8 rounded-sm shadow-sm text-center">
                <div className="w-14 h-14 rounded-full bg-[#b51a00] text-white flex items-center justify-center mx-auto mb-5 text-2xl font-black" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {i + 1}
                </div>
                <h3
                  className="text-xl font-bold uppercase tracking-tight mb-2 text-[#0f0f0f]"
                  style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                >
                  {s.title}
                </h3>
                <p className="text-[#4a4a4a]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="discount-faq" className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">Good To Know</span>
            <h2
              className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 text-[#0f0f0f]"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              Discount FAQs
            </h2>
            <div className="section-divider mx-auto mt-4"></div>
          </div>

          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i} data-testid={`discount-faq-${i}`} className="bg-[#f4f4f5] p-6 rounded-sm border-l-4 border-[#b51a00]">
                <h3 className="font-bold text-[#0f0f0f] flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-[#b51a00] shrink-0 mt-0.5" />
                  {f.question}
                </h3>
                <p className="text-[#4a4a4a] mt-2 pl-7">{f.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#b51a00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BadgePercent className="w-12 h-12 text-white/90 mx-auto mb-4" />
          <h2
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            {c.cta?.heading || defaultDiscountContent.cta.heading}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {c.cta?.text || defaultDiscountContent.cta.text}
          </p>
          <Link to="/contact">
            <Button
              data-testid="discount-cta-btn"
              className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
            >
              Get Your Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
